import * as turf from '@turf/turf';
import dissolve from '@turf/dissolve';
import polygonize from '@turf/polygonize';
import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
  Polygon as GJPolygon,
  MultiPolygon,
  LineString,
  MultiLineString,
} from 'geojson';

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

function ensureClosed(ring: [number, number][] | null | undefined): [number, number][] {
  if (!Array.isArray(ring) || ring.length === 0) return [];
  const clean: [number, number][] = ring.filter(
    (p): p is [number, number] => Array.isArray(p) && isNum(p[0]) && isNum(p[1])
  );
  if (clean.length === 0) return [];
  const [fx, fy] = clean[0];
  const [lx, ly] = clean[clean.length - 1];
  return fx === lx && fy === ly ? clean : [...clean, [fx, fy]];
}
function signedArea(ring: [number, number][]): number {
  const r = ensureClosed(ring);
  if (r.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < r.length - 1; i++) {
    const [x1, y1] = r[i],
      [x2, y2] = r[i + 1];
    sum += (x2 - x1) * (y2 + y1);
  }
  return -sum;
}
export function isClockwise(ring: [number, number][] | null | undefined): boolean {
  const r = ensureClosed(ring ?? []);
  if (r.length < 3) return true;
  return signedArea(r) < 0;
}
export function enforceLeftHandRings(coords: [number, number][][] | null | undefined): [number, number][][] {
  if (!Array.isArray(coords) || coords.length === 0) return [];
  const out: [number, number][][] = [];
  const outer = ensureClosed(coords[0]);
  if (outer.length >= 4) out.push(isClockwise(outer) ? outer : [...outer].reverse());
  for (let i = 1; i < coords.length; i++) {
    const hole = ensureClosed(coords[i]);
    if (hole.length >= 4) out.push(!isClockwise(hole) ? hole : [...hole].reverse());
  }
  return out;
}

export function splitToPolygons(
  f: Feature<Geometry, GeoJsonProperties> | null,
  props: Record<string, any> = {}
): Feature<GJPolygon>[] {
  if (!f || !f.geometry) return [];
  const g = f.geometry;
  if (g.type === 'Polygon') {
    const c = enforceLeftHandRings(g.coordinates as any);
    return c.length
      ? [
          {
            type: 'Feature',
            properties: { ...(f.properties ?? {}), ...props },
            geometry: { type: 'Polygon', coordinates: c },
          },
        ]
      : [];
  }
  if (g.type === 'MultiPolygon') {
    const out: Feature<GJPolygon>[] = [];
    for (const part of g.coordinates as [number, number][][][]) {
      const c = enforceLeftHandRings(part);
      if (c.length)
        out.push({
          type: 'Feature',
          properties: { ...(f.properties ?? {}), ...props },
          geometry: { type: 'Polygon', coordinates: c },
        });
    }
    return out;
  }
  if (g.type === 'GeometryCollection') {
    const out: Feature<GJPolygon>[] = [];
    for (const piece of g.geometries ?? [])
      out.push(...splitToPolygons({ type: 'Feature', properties: f.properties ?? {}, geometry: piece }, props));
    return out;
  }
  return [];
}

export function dissolveToPolygons(features: Feature<GJPolygon>[]): Feature<GJPolygon>[] {
  return splitToPolygons(dissolveCluster(features), { shape: 'polygon', _circle: false });
}

function dissolveCluster(features: Feature<GJPolygon>[]): Feature<Geometry> | null {
  if (!features.length) return null;
  const fc: FeatureCollection = { type: 'FeatureCollection', features: features as any };
  try {
    const merged = turf.union(fc as any);
    if (merged) return { ...merged, properties: { ...(features[0].properties ?? {}) } };
  } catch {}

  try {
    const dFC = dissolve(fc as any) as FeatureCollection<GJPolygon | MultiPolygon>;
    if (dFC?.features?.length) {
      let merged: Feature<Geometry> | null = null;
      if (dFC.features.length === 1) merged = dFC.features[0] as unknown as Feature<Geometry>;
      else {
        const parts: [number, number][][][] = [];
        for (const f of dFC.features) {
          if (f.geometry.type === 'Polygon') parts.push(f.geometry.coordinates as any);
          else if (f.geometry.type === 'MultiPolygon') for (const p of f.geometry.coordinates as any) parts.push(p);
        }
        merged =
          parts.length === 1
            ? { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: parts[0] } }
            : ({
                type: 'Feature',
                properties: {},
                geometry: { type: 'MultiPolygon', coordinates: parts },
              } as Feature<Geometry>);
      }
      try {
        const b0 = turf.buffer(merged as any, 0) as any as Feature<Geometry>;
        if (b0?.geometry) return b0;
      } catch {}
      return merged;
    }
  } catch {}

  try {
    const lines = turf.polygonToLine(fc as any) as FeatureCollection<LineString | MultiLineString>;
    const polyFC = polygonize(lines as any) as FeatureCollection<GJPolygon>;
    if (polyFC?.features?.length) {
      let merged: Feature<Geometry> | null;
      if (polyFC.features.length === 1) merged = polyFC.features[0] as unknown as Feature<Geometry>;
      else {
        const parts: [number, number][][][] = [];
        for (const f of polyFC.features) parts.push(f.geometry.coordinates as any);
        merged = {
          type: 'Feature',
          properties: {},
          geometry: { type: 'MultiPolygon', coordinates: parts },
        } as Feature<Geometry>;
      }
      try {
        const b0 = turf.buffer(merged as any, 0) as any as Feature<Geometry>;
        if (b0?.geometry) return b0;
      } catch {}
      return merged;
    }
  } catch {}

  return null;
}
